import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { useScreenWidth } from "../../helper/screentWidth";
import { FaEye, FaEyeSlash } from "react-icons/fa";

function CustomTextField({
  Title,
  Active,
  Type,
  value,
  onChange,
  placeholder,
  firstmeeting = false,
  currency = false,
}) {
  const screenWidth = useScreenWidth();
  const [showPassword, setShowPassword] = useState(false);

  const formatDateValue = (dateValue) => {
    if (!dateValue) return "";

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split("-");
      return `${month}/${day}/${year}`;
    }

    // Try to parse as Date object
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    }

    return dateValue;
  };

  const convertToHTMLDateFormat = (mmddyyyy) => {
    if (!mmddyyyy) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(mmddyyyy)) {
      return mmddyyyy;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(mmddyyyy)) {
      const [month, day, year] = mmddyyyy.split("/");
      return `${year}-${month}-${day}`;
    }

    return mmddyyyy;
  };

  const formatCurrencyValue = (value) => {
    if (!value) return "";

    const digitsOnly = value.toString().replace(/\D/g, "");
    return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const handleChange = useCallback(
    (e) => {
      let newValue = e.target.value;

      if (currency) {
        const digitsOnly = newValue.replace(/\D/g, "");
        onChange(digitsOnly);
        return;
      }

      if (Type === "date") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(newValue)) {
          const [year, month, day] = newValue.split("-");
          newValue = `${month}/${day}/${year}`;
        }
      }

      onChange(newValue);
    },
    [onChange, Type, currency]
  );

  const isPasswordField = Type === "password";
  const isDateField = Type === "date";

  let displayType = Type;
  if (isPasswordField && showPassword) {
    displayType = "text";
  }

  let displayValue = value;
  if (currency && Active) {
    const formatted = formatCurrencyValue(value);
    displayValue = formatted ? `$${formatted}` : "";
  } else if (currency) {
    const formatted = formatCurrencyValue(value);
    displayValue = formatted ? `$${formatted}` : "Empty";
  } else if (isDateField) {
    displayValue = convertToHTMLDateFormat(value);
  }

  return (
    <Container firstmeeting={firstmeeting}>
      <FieldName firstmeeting={firstmeeting}>{Title}</FieldName>
        <FieldContainer>
          <CustomField
            value={displayValue}
            disabled={!Active}
            type={displayType ? displayType : "text"}
            onChange={handleChange}
            placeholder={placeholder}
          />
          {isPasswordField && (
            <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </PasswordToggle>
          )}
        </FieldContainer>
      
    </Container>
  );
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr ;
  align-items: center;
  width: 100%;
  @media screen and (max-width: ${props => props.firstmeeting ? "983px" : "690px"}) {
    grid-template-columns: 1fr;
    gap: 10px;
    max-height: 100px;
  }
`;

const FieldName = styled.div`
  text-align: right;
    justify-self: start;
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 14px;
  font-style: italic;
  font-weight: 300;
  @media screen and (max-width: ${(props) =>
      props.firstmeeting ? "983px" : "690px"}) {
    padding: 20px 0px 0px 0px;
  }
`;

const Text = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 5px;
  min-width: 80px;
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
`;

const FieldContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const PasswordToggle = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #304961;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;

  &:hover {
    color: #1a2c3d;
  }
`;

const CustomField = styled.input`
  outline: none;
  border: none;
  padding-left: 10px;
  background-color: var(--editable);
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
  min-width: 80px;
  height: 30px;
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  width: 100%;
  border-radius:20px;
  &::placeholder {
    font-size: 14px;
    font-weight: 400;
    color: #8a9ba8;
  }
`;

export default CustomTextField;
